import { useState, useMemo } from "react";

function normalizeInternalOptins(config) {
  if (!config || !config.steps.userForm.internalOptins) return { groups: [] };

  const groups = Array.isArray(config.steps.userForm.internalOptins.groups)
    ? config.steps.userForm.internalOptins.groups
        .filter(Boolean) // remove null/undefined
        .map((group) => {
          const safeOptions = Array.isArray(group.options)
            ? group.options.filter((opt) => {
                const hasValidKey =
                  typeof opt?.key === "string" && opt.key.trim() !== "";
                const hasValidLabel =
                  typeof opt?.label === "string" && opt.label.trim() !== "";
                return hasValidKey && hasValidLabel;
              })
            : [];

          return {
            ...group,
            options: safeOptions,
          };
        })
        .filter((group) => group.options.length > 0) // ignore empty groups
    : [];

  return groups;
}

export function useInternalOptins(config) {
  const groups = normalizeInternalOptins(config);

  // 1) Initialise la state
  const initialState = useMemo(
    () =>
      Object.fromEntries(
        groups.flatMap((group) =>
          group.options
            .filter((opt) => opt.key) // ignore config template placeholders
            .map((opt) => [opt.key, false])
        )
      ),
    [groups]
  );

  const [values, setValues] = useState(initialState);

  // 2) Toggle checkbox
  const toggle = (key) => {
    setValues((prev) => {
      const next = { ...prev, [key]: !prev[key] };

      // gestion automatique du XOR
      const group = groups.find((g) =>
        g.options.some((opt) => opt.key === key)
      );

      if (group?.type === "xor" && next[key]) {
        group.options
          .filter((opt) => opt.key !== key)
          .forEach((opt) => (next[opt.key] = false));
      }

      return next;
    });
  };

  // 3. Form validation
  const validation = useMemo(() => {
    const errors = {};

    groups.forEach(group => {
      const selectedCount = group.options.filter(opt => values[opt.key]).length;

      if (group.type === "xor" && selectedCount !== 1) {
        errors[group.id] = `${group.label}: Veuillez sélectionner une option.`;
      } else if (group.required && selectedCount === 0) {
        errors[group.id] = `${group.label}: Veuillez sélectionner au moins une option.`;
      } else {
        errors[group.id] = null;
      }
    });

    return {
      isValid: Object.values(errors).every(msg => msg === null),
      errors
    };
  }, [values, groups]);

  return {
    groups,
    values,
    toggle,
    validation,
    reset: () => setValues(initialState),
  };
}
